import { useCallback, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Typography from "@mui/material/Typography";

const PaginationButton = styled(Button)(({ theme }) => ({
  minWidth: "100px",
  "&:disabled": {
    color: theme.palette.text.disabled,
    borderColor: theme.palette.action.disabled,
    "& .MuiSvgIcon-root": {
      color: theme.palette.action.disabled,
    },
  },
}));

const PageIndicator = styled("div")({
  display: "flex",
  alignItems: "center",
  margin: "0 16px",
  minWidth: "80px",
  justifyContent: "center",
});

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
}) => {
  const storefrontRef = useRef<HTMLDivElement>(null);
  const previousPageRef = useRef<number>(currentPage);

  const scrollToStorefront = useCallback(() => {
    const storefrontElement = document.querySelector(".storefront-container");
    if (!storefrontElement) return;

    const headerOffset = 100;
    const elementPosition = storefrontElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page !== currentPage) {
        onPageChange(page);
        setTimeout(scrollToStorefront, 0);
      }
    },
    [onPageChange, currentPage, scrollToStorefront]
  );

  useEffect(() => {
    if (
      previousPageRef.current !== currentPage &&
      previousPageRef.current !== 0
    ) {
      const timer = setTimeout(() => {
        scrollToStorefront();
      }, 50);

      return () => clearTimeout(timer);
    }
    previousPageRef.current = currentPage;
  }, [currentPage, scrollToStorefront]);

  if (totalPages <= 1) return null;

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  return (
    <div
      className={`d-flex justify-content-center align-items-center mt-4 ${className}`}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <PaginationButton
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={(e) => {
            e.preventDefault();
            handlePageChange(currentPage - 1);
          }}
          disabled={isFirstPage}
        >
          Previous
        </PaginationButton>

        <PageIndicator>
          <Typography variant="body1">
            {currentPage} of {totalPages}
          </Typography>
        </PageIndicator>

        <PaginationButton
          variant="outlined"
          endIcon={<ArrowForwardIcon />}
          onClick={(e) => {
            e.preventDefault();
            handlePageChange(currentPage + 1);
          }}
          disabled={isLastPage}
        >
          Next
        </PaginationButton>
      </Stack>
    </div>
  );
};

export default PaginationControls;
